import React from 'react';
import UnifiedDateRangePicker from '../../common/UnifiedDateRangePicker';
import ThemedSelect from '../../common/ThemedSelect';

const FilterControls = ({
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  selectedGrouping,
  setSelectedGrouping,
  groupings,
  timingFilter,
  setTimingFilter,
  onApply,
  loading,
  loadingGroupings,
}) => {
  // Generate timing options (1-12 hours + "No filter")
  const timingOptions = ['No filter', ...Array.from({ length: 12 }, (_, i) => (i + 1).toString())];

  return (
    <div className="bg-surface p-6 rounded-xl shadow-sm border border-gray-200 mb-6">
      {/* Date Range Section */}
      <div className="mb-6 pb-6 border-b border-gray-200">
        <div className="mb-4">
          <h3 className="text-sm font-bold text-gray-900 mb-1">Date Range</h3>
          <p className="text-xs text-gray-600">Select the period for transaction analysis</p>
        </div>
        <div className="flex justify-start">
          <UnifiedDateRangePicker
            startDate={startDate}
            onStartDateChange={(date) => {
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              setStartDate(`${year}-${month}-${day}`);
            }}
            endDate={endDate}
            onEndDateChange={(date) => {
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              setEndDate(`${year}-${month}-${day}`);
            }}
            disabled={loading || loadingGroupings}
          />
        </div>
      </div>

      {/* Filter Options Section */}
      <div>
        <h3 className="text-sm font-bold text-gray-900 mb-4">Filter Options</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Grouping Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Route Grouping</label>
            <ThemedSelect
              value={selectedGrouping?.id || ''}
              onChange={(e) => {
                const grouping = groupings.find((g) => g.id === parseInt(e.target.value));
                setSelectedGrouping(grouping || null);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              disabled={loading || loadingGroupings}
            >
              <option value="">Select a grouping...</option>
              {groupings.map((grouping) => (
                <option key={grouping.id} value={grouping.id}>
                  {grouping.name}
                </option>
              ))}
            </ThemedSelect>
          </div>

          {/* Timing Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Timing Filter</label>
            <ThemedSelect
              value={timingFilter}
              onChange={(e) => setTimingFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              disabled={loading || loadingGroupings}
            >
              {timingOptions.map((option) => (
                <option key={option} value={option}>
                  {option === 'No filter'
                    ? 'No filter'
                    : `${option} hour${option === '1' ? '' : 's'}`}
                </option>
              ))}
            </ThemedSelect>
          </div>
        </div>
      </div>

      {/* Apply Button */}
      <div className="mt-6 flex justify-end">
        <button
          onClick={onApply}
          disabled={!selectedGrouping || loading || loadingGroupings}
          className="px-6 py-2 bg-primary-600 text-white rounded-md shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Loading...' : 'Apply Filters'}
        </button>
      </div>

      {/* Filter Summary */}
      {selectedGrouping && (
        <div className="mt-4 p-3 bg-gray-50 rounded-md">
          <p className="text-sm text-gray-600">
            <strong>Selected:</strong> {selectedGrouping.name} |<strong> Date Range:</strong>{' '}
            {startDate} to {endDate} |<strong> Timing:</strong>{' '}
            {timingFilter === 'No filter'
              ? 'No timing filter'
              : `Within ${timingFilter} hour${timingFilter === '1' ? '' : 's'}`}
          </p>
        </div>
      )}
    </div>
  );
};

export default FilterControls;
